/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import { Step, GiftOrder, SenderInfo, RecipientInfo, GiftItem, Vendor, OrderItem, SubscriptionOption } from './types';
import LandingPage from './pages/LandingPage';
import PathSelectionPage from './pages/PathSelectionPage';
import SenderPage from './pages/SenderPage';
import RecipientPage from './pages/RecipientPage';
import ItemsPage from './pages/ItemsPage';
import VendorPage from './pages/VendorPage';
import PaymentPage from './pages/PaymentPage';
import PaymentCallbackPage from './pages/PaymentCallbackPage';
import ConfirmationPage from './pages/ConfirmationPage';
import SubscriptionSelectionPage from './pages/SubscriptionSelectionPage';
import SubscriptionCustomizePage from './pages/SubscriptionCustomizePage';
import SubscriptionCheckoutPage from './pages/SubscriptionCheckoutPage';
import OffersIndexPage from './pages/offers/OffersIndexPage';
import OfferPage from './pages/offers/OfferPage';
import OfferOrderPage from './pages/offers/OfferOrderPage';
import OfferConfirmationPage from './pages/offers/OfferConfirmationPage';
import OfferManagePage from './pages/offers/OfferManagePage';
import { SiteHeader } from './components/bx';
import './styles/bx.css';
import HelpButton from './components/HelpButton';
import PlatformFooter from './components/PlatformFooter';
import ScrollToTop from './components/ScrollToTop';
import { Routes, Route, useNavigate, useLocation, Navigate, Outlet } from 'react-router-dom';
import { ROUTES } from './constants/routes';
import { useToast } from './components/Toast';
import { CheckoutGuard } from './components/CheckoutGuard';
import { clearSubscriptionCatalogCache } from './hooks/useSubscriptionCatalog';
import { clearVendorsCache, prefetchVendors } from './hooks/useVendors';

const defaultOrder: GiftOrder = {
  sender: { email: '', fullName: '', phone: '' },
  recipient: { email: '', fullName: '', phone: '', address: '', city: '', state: '', message: '' },
  items: [],
  vendor: null,
  isGetMe: false,
  isSubscribe: false,
};

const STORAGE_KEY = 'boqbox_gift_order_v1';
const PAYMENT_DATA_STORAGE_KEY = 'boqbox_payment_data_v1';
const PAYMENT_REFERENCE_STORAGE_KEY = 'boqbox_payment_reference_v1';

interface CheckoutLayoutProps {
  isDesktop: boolean;
  showOverlay?: boolean;
  onStartSendGift: () => void;
  onStartGetMe: () => void;
  onNavigateToSelection: () => void;
  onLogoClick: () => void;
  onOverlayClick: () => void;
}

// Checkout steps are full pages now (they used to float as a modal over the landing page on desktop).
function CheckoutLayout(_props: CheckoutLayoutProps) {
  return <Outlet />;
}

// Frame for the subscription and confirmation pages, which are laid out as a single centred column.
function ColumnShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="bx bx-column-page">
      <SiteHeader />
      <main className="bx-column">{children}</main>
    </div>
  );
}

// Old /specialmealoffer/... links (emails, shared links, Paystack returns) keep working under /offers.
function LegacyOffersRedirect() {
  const loc = useLocation();
  return <Navigate to={`${loc.pathname.replace(/^\/specialmealoffer/, '/offers')}${loc.search}`} replace />;
}

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const subscriptionFlowRoutes = new Set<string>([
    ROUTES.TIERS,
    ROUTES.SUBSCRIBE,
    ROUTES.VENDOR,
    ROUTES.SENDER,
    ROUTES.RECIPIENT,
    ROUTES.PAYMENT,
    ROUTES.PAYMENT_CALLBACK,
    ROUTES.CONFIRMATION,
  ]);

  const getInitialOrder = (): GiftOrder => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object' && 'sender' in parsed && 'recipient' in parsed && 'items' in parsed) {
          return parsed as GiftOrder;
        }
      }
    } catch (e) {
      console.warn('Failed to rehydrate order from sessionStorage:', e);
    }
    return defaultOrder;
  };

  const getInitialPaymentData = (): any => {
    try {
      const saved = sessionStorage.getItem(PAYMENT_DATA_STORAGE_KEY);
      if (!saved) {
        return null;
      }

      return JSON.parse(saved);
    } catch (e) {
      console.warn('Failed to rehydrate payment data from sessionStorage:', e);
      return null;
    }
  };

  const getInitialPaymentReference = (): string | null => {
    try {
      return sessionStorage.getItem(PAYMENT_REFERENCE_STORAGE_KEY);
    } catch (e) {
      console.warn('Failed to rehydrate payment reference from sessionStorage:', e);
      return null;
    }
  };

  const [order, setOrder] = useState<GiftOrder>(getInitialOrder);
  const [paymentReference, setPaymentReference] = useState<string | null>(getInitialPaymentReference);
  const [paymentData, setPaymentData] = useState<any>(getInitialPaymentData);
  const [isDesktop, setIsDesktop] = useState<boolean>(window.innerWidth >= 1024);
  const isSubscriptionOrder =
    order.isSubscribe || Boolean(order.subscription) || order.items.some((item) => Boolean(item.subscriptionOption));
  const isSpecialMealRoute = location.pathname.startsWith(ROUTES.OFFERS);
  const shouldHideHelpButton =
    isSpecialMealRoute ||
    location.pathname === ROUTES.TIERS ||
    (isSubscriptionOrder && subscriptionFlowRoutes.has(location.pathname));

  // Persistence Side Effect
  const persistenceTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (persistenceTimeoutRef.current !== null) {
      window.clearTimeout(persistenceTimeoutRef.current);
    }

    persistenceTimeoutRef.current = window.setTimeout(() => {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(order));
      persistenceTimeoutRef.current = null;
    }, 250);

    return () => {
      if (persistenceTimeoutRef.current !== null) {
        window.clearTimeout(persistenceTimeoutRef.current);
      }
    };
  }, [order]);

  useEffect(() => {
    try {
      if (paymentData) {
        sessionStorage.setItem(PAYMENT_DATA_STORAGE_KEY, JSON.stringify(paymentData));
      } else {
        sessionStorage.removeItem(PAYMENT_DATA_STORAGE_KEY);
      }
    } catch (e) {
      console.warn('Failed to persist payment data to sessionStorage:', e);
    }
  }, [paymentData]);

  useEffect(() => {
    try {
      if (paymentReference) {
        sessionStorage.setItem(PAYMENT_REFERENCE_STORAGE_KEY, paymentReference);
      } else {
        sessionStorage.removeItem(PAYMENT_REFERENCE_STORAGE_KEY);
      }
    } catch (e) {
      console.warn('Failed to persist payment reference to sessionStorage:', e);
    }
  }, [paymentReference]);

  // Handle window resize for responsive desktop/mobile switching
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check for payment callback on app load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reference = params.get('reference');
    const stepParam = params.get('step');

    // Special meal offer pages handle their own Paystack return (/offers/:slug/confirmation).
    if (location.pathname.startsWith(`${ROUTES.OFFERS}/`) || location.pathname.startsWith('/specialmealoffer')) {
      return;
    }

    if (reference || stepParam === 'payment-callback') {
      if (reference) {
        setPaymentReference(reference);
      }
      navigate(ROUTES.PAYMENT_CALLBACK);
    }
  }, [location.pathname, location.search, navigate]);

  const updateSender = (sender: SenderInfo) => setOrder((o) => ({ ...o, sender }));
  const updateRecipient = (recipient: RecipientInfo) => setOrder((o) => ({ ...o, recipient }));
  
  const updateItems = (items: OrderItem[]) => setOrder((o) => ({ ...o, items }));
  const addItem = (item: GiftItem, quantity: number, subscriptionOption?: SubscriptionOption) => {
    setOrder((o) => {
      const itemId = item._id || item.id;
      const existingIndex = o.items.findIndex((oi) => (oi.item._id || oi.item.id) === itemId);
      if (existingIndex >= 0) {
        const newItems = [...o.items];
        newItems[existingIndex].quantity += quantity;
        if (subscriptionOption) {
          newItems[existingIndex].subscriptionOption = subscriptionOption;
        }
        return { ...o, items: newItems };
      }
      return { ...o, items: [...o.items, { item, quantity, subscriptionOption }] };
    });
  };
  
  const updateVendor = (vendor: Vendor) => setOrder((o) => ({ ...o, vendor }));
  const updateSubscription = (subscription: GiftOrder['subscription']) => setOrder((o) => ({ ...o, subscription }));

  const getEmojiForCategory = (category: string): string => {
    const emojiMap: { [key: string]: string } = {
      lunch: '🍱',
      breakfast: '🥚',
      dinner: '🍽️',
      snacks: '🍿',
      beverages: '🥤',
      desserts: '🍰',
      protein: '🥩',
      produce: '🍎',
      groceries: '🧺',
      baby: '🍼',
    };

    return emojiMap[category.toLowerCase()] || '🎁';
  };

  const buildSubscriptionVariantLabel = (
    subscriptionPackageItem: NonNullable<NonNullable<GiftOrder['subscription']>['packageItems']>[number]
  ): string => {
    const parts = [
      subscriptionPackageItem.type,
      subscriptionPackageItem.brand,
      subscriptionPackageItem.size,
      subscriptionPackageItem.color,
    ].filter((part): part is string => Boolean(part && part.trim()));

    if (parts.length > 0) {
      return parts.join(' · ');
    }

    return `${subscriptionPackageItem.qty} units`;
  };

  const preloadSubscriptionPackage = async (
    subscription: NonNullable<GiftOrder['subscription']>
  ): Promise<OrderItem[]> => {
    const packageItems = subscription.packageItems ?? [];

    if (packageItems.length === 0) {
      return [];
    }

    return packageItems.map((subscriptionPackageItem) => {
      const itemPrice = subscriptionPackageItem.item.price ?? 0;

      return {
        item: {
          id: subscriptionPackageItem.item._id,
          _id: subscriptionPackageItem.item._id,
          name: subscriptionPackageItem.item.name,
          description:
            buildSubscriptionVariantLabel(subscriptionPackageItem) || 'Subscription package item',
          price: itemPrice,
          category: 'subscription',
          type: 'subscription',
          image_url: '',
          emoji: '📦',
        },
        quantity: subscriptionPackageItem.qty,
        subscriptionOption: {
          name: subscription.frequency || 'subscription',
          price: itemPrice,
          quantity: buildSubscriptionVariantLabel(subscriptionPackageItem),
        },
      } satisfies OrderItem;
    });
  };

  const handleReset = () => {
    clearSubscriptionCatalogCache();
    clearVendorsCache();
    setPaymentData(null);
    setPaymentReference(null);
    sessionStorage.removeItem(PAYMENT_DATA_STORAGE_KEY);
    sessionStorage.removeItem(PAYMENT_REFERENCE_STORAGE_KEY);
    setOrder({ ...defaultOrder, isSubscribe: false, isGetMe: false });
    navigate(ROUTES.LANDING, { replace: true });
  };

  const handleLogoClick = () => {
    clearVendorsCache();
    navigate(ROUTES.LANDING, { replace: true });
    setPaymentData(null);
    setPaymentReference(null);
    sessionStorage.removeItem(PAYMENT_DATA_STORAGE_KEY);
    sessionStorage.removeItem(PAYMENT_REFERENCE_STORAGE_KEY);
    setOrder({ ...defaultOrder, isSubscribe: false, isGetMe: false });
  };

  const handleStartSendGift = () => {
    setOrder((o) => ({ ...o, isGetMe: false, isSubscribe: false, items: [], vendor: null }));
    navigate(ROUTES.ITEMS);
  };

  const handleStartGetMe = () => {
    setOrder((o) => ({ ...o, isGetMe: true, isSubscribe: false, items: [], vendor: null }));
    navigate(ROUTES.ITEMS);
  };

  const handleStartSubscribe = (category: 'consumer' | 'b2b' = 'consumer') => {
    clearSubscriptionCatalogCache();
    clearVendorsCache('subscription');
    setOrder((o) => ({ ...o, isGetMe: false, isSubscribe: true, items: [], vendor: null, subscription: undefined }));
    navigate(`${ROUTES.TIERS}?tab=${category}`);
  };

  const handleNavigateToSelection = () => {
    navigate(ROUTES.PATH_SELECTION);
  };

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Top-level pages */}
        <Route path={ROUTES.LANDING} element={
          <LandingPage 
            onStart={handleStartSendGift}
            onGetMe={handleStartGetMe}
            onSubscribe={handleNavigateToSelection}
            onLogoClick={handleLogoClick}
          />
        } />
        
        <Route path={ROUTES.PATH_SELECTION} element={
          <PathSelectionPage onSubscribe={handleStartSubscribe} onLogoClick={handleLogoClick} />
        } />

        <Route path={ROUTES.OFFERS} element={<OffersIndexPage />} />
        <Route path={ROUTES.OFFER} element={<OfferPage />} />
        <Route path={ROUTES.OFFER_ORDER} element={<OfferOrderPage />} />
        <Route path={ROUTES.OFFER_CONFIRMATION} element={<OfferConfirmationPage />} />
        <Route path={ROUTES.OFFER_MANAGE} element={<OfferManagePage />} />
        {/* Unknown offer sub-pages land on the offers page; old /specialmealoffer links move to /offers. */}
        <Route path={`${ROUTES.OFFERS}/*`} element={<Navigate to={ROUTES.OFFERS} replace />} />
        <Route path="/specialmealoffer/*" element={<LegacyOffersRedirect />} />
        <Route path="/specialmealoffer" element={<LegacyOffersRedirect />} />

        <Route element={
          <CheckoutLayout
            isDesktop={isDesktop}
            onStartSendGift={handleStartSendGift}
            onStartGetMe={handleStartGetMe}
            onNavigateToSelection={handleNavigateToSelection}
            onLogoClick={handleLogoClick}
            onOverlayClick={() => navigate(ROUTES.LANDING)}
          />
        }>
          <Route path={ROUTES.TIERS} element={
            <ColumnShell><SubscriptionSelectionPage 
              onSelectTier={async (metadata: GiftOrder['subscription']) => {
                if (!metadata) {
                  throw new Error('The selected subscription tier is missing its metadata.');
                }

                clearSubscriptionCatalogCache();
                const preloadedItems =
                  metadata.packageItems && metadata.packageItems.length > 0
                    ? await preloadSubscriptionPackage(metadata)
                    : [];
                setOrder((o) => ({
                  ...o,
                  isGetMe: false,
                  isSubscribe: true,
                  subscription: {
                    ...metadata,
                    frequency: metadata.frequency,
                    minThreshold: metadata.minThreshold,
                    packageTotalPrice: metadata.packageTotalPrice,
                    packageItems: metadata.packageItems,
                  },
                  items: preloadedItems,
                }));
                void prefetchVendors('subscription');
                navigate(ROUTES.SUBSCRIBE);
              }} 
            /></ColumnShell>
          } />
        </Route>

        {/* Nested Checkout Flow with stable background and Guard protection */}
        <Route element={
          <CheckoutGuard order={order}>
            <CheckoutLayout
              isDesktop={isDesktop}
              onStartSendGift={handleStartSendGift}
              onStartGetMe={handleStartGetMe}
              onNavigateToSelection={handleNavigateToSelection}
              onLogoClick={handleLogoClick}
              onOverlayClick={() => navigate(ROUTES.LANDING)}
            />
          </CheckoutGuard>
        }>
          <Route path={ROUTES.ITEMS} element={
            <ItemsPage
              selectedItems={order.items}
              isGetMe={order.isGetMe}
              onAddItem={addItem}
              onUpdateItems={updateItems}
              onContinue={() => navigate(ROUTES.VENDOR)}
              onBack={() => navigate(ROUTES.LANDING)}
            />
          } />

          <Route path={ROUTES.SUBSCRIBE} element={
            <ColumnShell><SubscriptionCustomizePage
              selectedItems={order.items}
              subscription={order.subscription}
              onUpdateItems={updateItems}
              onSubscriptionChange={updateSubscription}
              onContinue={() => navigate(ROUTES.VENDOR)}
              onBack={() =>
                navigate(`${ROUTES.TIERS}?tab=${order.subscription?.category ?? 'consumer'}`)
              }
            /></ColumnShell>
          } />

          <Route path={ROUTES.VENDOR} element={
            <VendorPage
              isGetMe={order.isGetMe}
              isSubscribe={isSubscriptionOrder}
              selectedVendor={order.vendor}
              onVendorChange={updateVendor}
              onContinue={() => navigate(isSubscriptionOrder ? ROUTES.PAYMENT : ROUTES.SENDER)}
              onBack={() => navigate(isSubscriptionOrder ? ROUTES.SUBSCRIBE : ROUTES.ITEMS)}
            />
          } />

          <Route path={ROUTES.SENDER} element={
            <SenderPage
              data={order.sender}
              isGetMe={order.isGetMe}
              isSubscribe={order.isSubscribe}
              onChange={updateSender}
              onContinue={() => navigate(ROUTES.RECIPIENT)}
              onBack={() => navigate(ROUTES.VENDOR)}
            />
          } />

          <Route path={ROUTES.RECIPIENT} element={
            <RecipientPage
              data={order.recipient}
              senderInfo={order.sender}
              isGetMe={order.isGetMe}
              isSubscribe={order.isSubscribe}
              onChange={updateRecipient}
              onContinue={() => navigate(ROUTES.PAYMENT)}
              onBack={() => navigate(ROUTES.SENDER)}
            />
          } />

          <Route path={ROUTES.PAYMENT} element={
            isSubscriptionOrder ? (
              <ColumnShell><SubscriptionCheckoutPage
                items={order.items}
                vendor={order.vendor}
                sender={order.sender}
                recipient={order.recipient}
                subscription={order.subscription}
                onSenderChange={updateSender}
                onRecipientChange={updateRecipient}
                onSubscriptionChange={updateSubscription}
                onBack={() => navigate(ROUTES.VENDOR)}
              /></ColumnShell>
            ) : (
              <PaymentPage
                isGetMe={order.isGetMe}
                items={order.items}
                vendor={order.vendor!}
                sender={order.sender}
                recipient={order.recipient}
                onSubmit={() => navigate(ROUTES.PAYMENT_CALLBACK)}
                onBack={() => navigate(ROUTES.RECIPIENT)}
              />
            )
          } />
        </Route>

        {/* Specialized steps - no overlay background needed for these final confirmation steps */}
        <Route path={ROUTES.PAYMENT_CALLBACK} element={
          <>
            <PaymentCallbackPage
              reference={paymentReference}
              isSubscribe={isSubscriptionOrder}
              onPaymentConfirmed={(success, data) => {
                if (success) {
                  toast.showSuccess('Payment done successfully');
                  setPaymentData(data);
                  setTimeout(() => {
                    navigate(ROUTES.CONFIRMATION, { replace: true });
                  }, 250);
                } else {
                  toast.showError('Payment verification failed. Please try again.');
                  setPaymentReference(null);
                  setTimeout(() => navigate(ROUTES.PAYMENT, { replace: true }), 400);
                }
              }}
            />
            {toast.component}
          </>
        } />

        <Route path={ROUTES.CONFIRMATION} element={
          <CheckoutGuard order={order}>
            <ColumnShell>
              <ConfirmationPage
                order={order}
                paymentData={paymentData}
                paymentReference={paymentReference}
                onReset={handleReset}
              />
              {toast.component}
            </ColumnShell>
          </CheckoutGuard>
        } />

        {/* Fallback to landing */}
        <Route path="*" element={<Navigate to={ROUTES.LANDING} replace />} />
      </Routes>
      <PlatformFooter />
      {!shouldHideHelpButton ? <HelpButton /> : null}
    </>
  );
}
